import { HttpRequest } from '@angular/common/http';
import { Sort } from '@angular/material/sort';
import { ValueObject } from '@core/models/value-object.model';
import { serialize } from '@junte/serialize-ts';
import { getMetadata } from '@junte/serialize-ts/dist/metadata/get-metadata';
import { Observable } from 'rxjs';

import { DTO } from './api/dto';
import { EnvConfig } from './config';
import { BaseModel } from './models/base-model';
import { FileObject } from './models/file-object.model';

export const getValueAtPath = (keypath: string, obj: object): unknown => {
  if (!keypath) {
    return null;
  }

  return keypath.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
};

export const isCollection = (body: unknown) => body instanceof Array;

export const pipe =
  <T>(...fns) =>
  (x): T =>
    fns.reduce((v, f) => f(v), x);

export const replaceMultipleSlashesWithSingleSlash = (str: string) => str.replace(/\{2,}/g, '/');

export const standardizeUrl = (url: string | undefined | null): string => {
  if (!url) {
    return '';
  }
  return pipe<string>(replaceMultipleSlashesWithSingleSlash, stripLeadingSlashes, stripTrailingSlashes)(url);
};

export const stripTrailingSlashes = (str: string) => str.replace(/\/+$/, '');

export const stripLeadingSlashes = (str: string) => str.replace(/^\//, '');

export const convertBlobToBase64 = (blob: Blob): Observable<string | ArrayBuffer> => {
  return new Observable<string | ArrayBuffer>((observer) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = (event: ProgressEvent<FileReader>) => {
      observer.next(event.target.result);
      observer.complete();
    };
  });
};

export function compareObjects<T>(a: T, b: T): boolean {
  return (a == null && b == null) || JSON.stringify(a) === JSON.stringify(b);
}

export function keepPropertiesWithNames<T extends object>(obj: T, names: Array<keyof T> = []): Partial<T> {
  const o = obj;

  for (const key in o) {
    if (Object.prototype.hasOwnProperty.call(o, key)) {
      if (!names.includes(key)) {
        delete o[key];
      }
    }
  }

  return o;
}

export function removePropertiesWithValues<T extends object>(obj: T, values = []): Partial<T> {
  const o = obj;

  for (const key in o) {
    if (Object.prototype.hasOwnProperty.call(o, key)) {
      if (values.includes(o[key])) {
        delete o[key];
      }
    }
  }

  return o;
}

export function removeNilProperties<T extends object>(obj: T): Partial<T> {
  const o = obj;

  for (const key in o) {
    if (Object.prototype.hasOwnProperty.call(o, key)) {
      if (o[key] === null || o[key] === undefined) {
        delete o[key];
      }
    }
  }

  return o;
}

export function removeUndefinedProperties<T extends object>(obj: T): Partial<T> {
  const o = obj;

  for (const key in o) {
    if (Object.prototype.hasOwnProperty.call(o, key)) {
      if (o[key] === undefined) {
        delete o[key];
      }
    }
  }

  return o;
}

export function removeDuplicates<T>(a: Array<T>, matcher?: (x: T, y: T) => boolean): Array<T> {
  if (!matcher) {
    matcher = (x: T, y: T) => x === y;
  }
  return (
    a &&
    a.reduce((previous, current) => {
      return previous.find((p) => matcher(p, current)) == null ? [...previous, current] : previous;
    }, [] as T[])
  );
}

export const toCamelCase = (str: string) => str?.toLowerCase().replace(/(_|-)([a-z])/g, (sub) => sub[1].toUpperCase());

export function isEmpty(val: string | number): boolean {
  if (val === null || val === undefined) {
    return true;
  }

  return !val.toString().trim();
}

export function joinValueObjectNames(classes: ValueObject[]): string {
  return joinValueObjectKeys(classes, 'name');
}

export function joinObjectValues<T extends object>(obj: T, delimiter = ';'): string {
  return Object.values(obj)
    .map((val) => {
      if (typeof val === 'object' && val !== null) {
        return joinObjectValues(val, delimiter);
      }
      return val;
    })
    .join(delimiter);
}

export function joinValueObjectKeys(classes: ValueObject[], property: string): string {
  return classes
    .map((item) => {
      return item[property];
    })
    .join(', ');
}

export function globalSortCompare<T>(a: T, b: T, sort: Sort): number {
  const sortOrder = sort.direction === 'asc' ? -1 : 1;
  const valueA = a[sort.active];
  const valueB = b[sort.active];

  const result = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
  return result * sortOrder;
}

/**
 * Map values of given object using mapping function
 */
export function mapValues<K, V>(obj: K, mapFn: (value: K[keyof K], key: keyof K) => V): Record<keyof K, V> {
  return Object.assign({}, ...Object.entries(obj).map(([k, v]) => ({ [k]: mapFn(v, k as keyof K) })));
}

/**
 * Extract form-safe value from a potentially serialized ValueObject
 *
 * @param value The value to extract
 * @returns The codename if it's a ValueObject, otherwise the original value
 */
export function extractFormValue(value: any): string {
  // Handle ValueObject serialization - extract codename if it's an object with codename
  if (value && typeof value === 'object' && 'codename' in value) {
    return value.codename;
  }
  return value;
}

/**
 * Create FormData from model
 *
 * Preserves model to DTO mapping. Useful for handling forms with file fields.
 *
 * NOTE: Currently it supports FileObjects in shallow model only.
 * NOTE 2: ValueObjects are ignored
 */
export function toFormData(model: BaseModel): FormData {
  const dto = removeUndefinedProperties(serialize(model)) as DTO;

  return Object.keys(dto).reduce((fd, dtoKey) => {
    const value: string | FileObject | ValueObject = getValue(dtoKey, dto, model);

    if (value instanceof FileObject) {
      fd.append(dtoKey, value, value.name);
    } else {
      if (!(value instanceof ValueObject)) {
        fd.append(dtoKey, value);
      }
    }

    return fd;
  }, new FormData());

  function getValue(dtoKey: string, dtoObj: DTO, modelObj: BaseModel): string | FileObject | ValueObject {
    const meta = getMetadata(modelObj);
    const modelKey = meta.filter((val) => val.jsonPropertyName === dtoKey)[0]?.modelPropertyName;

    if (modelKey && modelObj[modelKey] instanceof FileObject) {
      return modelObj[modelKey];
    } else {
      return dtoObj[dtoKey];
    }
  }
}

/**
 * "And" predicate function for Array.filter()
 *
 * Requires to pass filters object as 'thisArg' of Array.filter().
 *
 * @example
 *
 * const filters = { prop1: 'ohai', prop2: true };
 *
 * const arr = [
 *  { prop1: 'ohai', prop2: false, prop3: 'abc' },
 *  { prop1: 'ohai', prop2: true, prop3: 'def' },
 *  { prop1: 'ohai 2', prop2: true, prop3: 'ghi' },
 * ];
 *
 * const filtered = arr.filter(andPredicate, filters);
 * // [{ prop1: 'ohai', prop2: true, prop3: 'def' }]
 */
export function andPredicate(obj: {}): boolean {
  const filters: {} = this;

  return Object.entries(filters)
    .filter(([_key, value]) => value !== null)
    .every(([key, _value]) => obj[key] === filters[key]);
}

export function nestedObjectsSearchPredicate<T extends object>(data: T, filter: string): boolean {
  // Join all the nested object to string in order to allow search inside the nested object
  const dataString = joinObjectValues(data).toLowerCase();
  const filterLower = filter.toLowerCase();
  return dataString.indexOf(filterLower) != -1;
}

/**
 * Return intersection of two arrays containing unique values
 */
export function intersect<T extends string | number = string | number, U extends string | number = string | number>(
  a: T[],
  b: U[],
): (T | U)[] {
  const setB = new Set(b);
  return [...new Set(a)].filter((x) => setB.has(x as T & U));
}

export function isInternalApi(url: HttpRequest<unknown>['url'], apiUrl: EnvConfig['apiUrl']): boolean {
  let isInternal = false;

  try {
    const u = new URL(url);
    isInternal = url.startsWith(apiUrl) || u.hostname === 'localhost';
  } catch {
    isInternal = true;
  }

  return isInternal;
}

export function toSnakeCase(str: string) {
  return str.replace(/[A-Z]/, (match) => `_${match.toLowerCase()}`);
}

export function toNumber(value: number | string): number {
  if (value === null || value === undefined || typeof value === 'undefined') {
    return undefined;
  }

  const number = parseInt(value.toString?.());
  return isNaN(number) ? undefined : number;
}

export function openElementFullscreen(element: any): boolean {
  if (element.requestFullscreen) {
    element.requestFullscreen();
    return true;
  } else if (element.mozRequestFullScreen) {
    element.mozRequestFullScreen();
    return true;
  } else if (element.webkitRequestFullscreen) {
    element.webkitRequestFullscreen();
    return true;
  } else if (element.msRequestFullscreen) {
    element.msRequestFullscreen();
    return true;
  }

  return false;
}

export function exitFullscreen(): void {
  document.exitFullscreen();
}

export function getInitials(text: string | null | undefined): string {
  if (!text) {
    return '';
  }
  return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

/**
 * Normalize date to YYYY-MM-DD format without timezone shifting
 *
 * This function ensures that the selected date remains consistent
 * regardless of the user's timezone by using local date methods
 * instead of UTC methods.
 */
export function normalizeDate(date: Date | string | null | undefined): string | null {
  if (!date) {
    return null;
  }

  if (date instanceof Date) {
    // Use local date methods to avoid timezone shifting
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  if (typeof date === 'string') {
    // If it's already a string, try to parse and normalize it
    const parsedDate = parseDate(date);
    if (parsedDate && !isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Invalid date string - return null
    return null;
  }

  return date as string;
}

export function parseDate(date: Date | string | number | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);

  if (typeof date === 'string') {
    // Handle DD/MM/YYYY format
    const ddmmyyyy = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
    if (ddmmyyyy.test(date)) {
      const [day, month, year] = date.split('/').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(date);
  }
  return null;
}
